/**
 * FaqTab - FAQ section management with drag-and-drop reordering & collapsible items
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, ChevronUp, ChevronDown, GripVertical, ChevronRight } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableFaqItem({ item, index, total, onUpdate, onRemove, onMove }) {
  const [expanded, setExpanded] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id || `faq-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-gray-50 rounded-lg border border-gray-200" data-testid={`faq-item-${index}`}>
      {/* Header row - always visible */}
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
          {...attributes}
          {...listeners}
          data-testid={`faq-grip-${index}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        <button
          className="flex items-center gap-2 flex-1 text-left"
          onClick={() => setExpanded(!expanded)}
          data-testid={`faq-toggle-${index}`}
        >
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          <span className="font-medium text-gray-700 text-sm truncate">
            {index + 1}. {item.question || 'Untitled question'}
          </span>
        </button>

        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost" size="sm" className="h-7 w-7 p-0"
            onClick={() => onMove(index, -1)} disabled={index === 0}
            data-testid={`faq-up-${index}`}
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost" size="sm" className="h-7 w-7 p-0"
            onClick={() => onMove(index, 1)} disabled={index === total - 1}
            data-testid={`faq-down-${index}`}
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
            onClick={() => onRemove(index)}
            data-testid={`faq-delete-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Expandable content */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-200">
          <div>
            <Label className="text-xs">Question</Label>
            <Input
              value={item.question}
              onChange={(e) => onUpdate(index, 'question', e.target.value)}
              placeholder="Enter the question..."
              className="mt-1"
              data-testid={`faq-question-${index}`}
            />
          </div>
          <div>
            <Label className="text-xs">Answer</Label>
            <Textarea
              value={item.answer}
              onChange={(e) => onUpdate(index, 'answer', e.target.value)}
              rows={3}
              placeholder="Enter the answer..."
              className="mt-1"
              data-testid={`faq-answer-${index}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function FaqTab({ content, setContent }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const updateFaq = (field, value) => {
    setContent(prev => ({ ...prev, faq: { ...prev.faq, [field]: value } }));
  };

  const updateFaqItem = (index, field, value) => {
    setContent(prev => {
      const newItems = [...(prev.faq?.items || [])];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, faq: { ...prev.faq, items: newItems } };
    });
  };

  const addFaqItem = () => {
    setContent(prev => ({
      ...prev,
      faq: { ...prev.faq, items: [...(prev.faq?.items || []), { id: Date.now().toString(), question: 'New question?', answer: 'Answer goes here...' }] }
    }));
  };

  const removeFaqItem = (index) => {
    setContent(prev => ({ ...prev, faq: { ...prev.faq, items: prev.faq?.items?.filter((_, i) => i !== index) || [] } }));
  };

  const moveFaqItem = (index, direction) => {
    const newIndex = index + direction;
    const items = content.faq?.items || [];
    if (newIndex < 0 || newIndex >= items.length) return;
    setContent(prev => {
      const newItems = [...(prev.faq?.items || [])];
      [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
      return { ...prev, faq: { ...prev.faq, items: newItems } };
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const items = content.faq?.items || [];
    const oldIndex = items.findIndex(item => (item.id || `faq-${items.indexOf(item)}`) === active.id);
    const newIndex = items.findIndex(item => (item.id || `faq-${items.indexOf(item)}`) === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      setContent(prev => ({
        ...prev,
        faq: { ...prev.faq, items: arrayMove([...(prev.faq?.items || [])], oldIndex, newIndex) }
      }));
    }
  };

  const items = content.faq?.items || [];
  const itemIds = items.map((item, i) => item.id || `faq-${i}`);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>FAQ Section</CardTitle>
          <CardDescription>Manage frequently asked questions. Drag to reorder.</CardDescription>
        </div>
        <Button onClick={addFaqItem} variant="outline" data-testid="add-faq-btn"><Plus className="w-4 h-4 mr-2" />Add Question</Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Badge</Label><Input value={content.faq?.badge || ''} onChange={(e) => updateFaq('badge', e.target.value)} placeholder="e.g., FAQ" /></div>
          <div><Label>Title</Label><Input value={content.faq?.title || ''} onChange={(e) => updateFaq('title', e.target.value)} placeholder="e.g., Frequently Asked Questions" /></div>
          <div><Label>Subtitle</Label><Input value={content.faq?.subtitle || ''} onChange={(e) => updateFaq('subtitle', e.target.value)} placeholder="Supporting text..." /></div>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {items.map((item, index) => (
                <SortableFaqItem
                  key={item.id || `faq-${index}`}
                  item={item}
                  index={index}
                  total={items.length}
                  onUpdate={updateFaqItem}
                  onRemove={removeFaqItem}
                  onMove={moveFaqItem}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </CardContent>
    </Card>
  );
}
